/**
 * THE shopper-facing money breakdown of a placed order, as the server
 * computes it (GET /api/orders/<id>/totals, api/src/pricing/order_totals.py).
 *
 * Bug Intake #481 (https://trello.com/c/jWUmSkp4): one payroll order showed
 * four different totals on the cart, the confirmation page, the email and
 * the order page, because each surface added the figures up itself. Every
 * order surface now renders these server lines and this server total, and
 * none of them composes money on its own.
 *
 * Shape (dollars): { payment_method: "card" | "payroll" | "net_checkout", payment_label,
 * payment_note,
 * consolidate_tax_fee, lines: [{ key, label, amount }], total, ... }.
 * Deduction lines (coupon, free item, allowance) carry NEGATIVE amounts.
 */

/**
 * Fetch an order's totals. Resolves to null when the read fails (a refused
 * or missing order answers 404), so a caller can keep the rest of the page.
 */
export async function fetchOrderTotals(axios, orderId) {
  if (!axios || orderId == null) return null;
  try {
    const res = await axios.get(`/orders/${orderId}/totals`);
    const totals = res && res.data && res.data.totals;
    return totals && Array.isArray(totals.lines) ? totals : null;
  } catch (err) {
    return null;
  }
}

/** True when the order was paid by payroll deduction or invoice. */
export function isPayrollTotals(totals) {
  return Boolean(totals && totals.payment_method === "payroll");
}

/**
 * True when the order's total is NOT a card charge: payroll deduction /
 * invoice, or Net Checkout (Bug Intake #518). Such a total is labeled
 * "Total" and names its payment method, never "Amount (to Be) Charged".
 */
export function isNonCardTotals(totals) {
  return Boolean(
    totals &&
      (totals.payment_method === "payroll" ||
        totals.payment_method === "net_checkout")
  );
}

/**
 * The shopper-facing payment line, "Payment: <label>[. <note>]", from the
 * server's payment_label / payment_note. Empty when there is no breakdown.
 */
export function paymentLine(totals) {
  if (!totals || !totals.payment_label) return "";
  const note = totals.payment_note ? `. ${totals.payment_note}` : "";
  return `Payment: ${totals.payment_label}${note}`;
}

/**
 * The lines as OrderDetailView funding bands ({ label, amount_cents }).
 * Rounded once, here, from the server's two-decimal dollars.
 */
export function totalsToBands(totals) {
  if (!totals || !Array.isArray(totals.lines)) return [];
  return totals.lines.map((line) => ({
    label: line.label,
    amount_cents: Math.round(Number(line.amount || 0) * 100),
  }));
}

/** Dollars to the "0.00" string the confirmation page prints. */
export function formatAmount(amount) {
  return Math.abs(Number(amount || 0)).toFixed(2);
}
